import React, { useState } from "react";
import "./business.css";
import Footer from "../footer/footer";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { FaIndustry, FaLeaf, FaHandshake, FaCertificate } from "react-icons/fa";
import { GiSewingNeedle } from "react-icons/gi";
import { MdVerified } from "react-icons/md";

const OurBusiness = () => {
  const [selectedPdf, setSelectedPdf] = useState(null);

  const [openCertificate, setOpenCertificate] = useState(null);

  const certificateList = [
    {
      id: 1,
      title: "Organic Content Standard (OCS)",
      pdf: "./files/Certificate1.pdf",
      preview: "./files/Certificate2.png",
      description:
        "OCS certification verifies organically grown fiber content and ensures transparent supply chain tracking.",
      issuedBy: "Control Union Certifications",
      validity: "2025"
    },
    {
      id: 2,
      title: "Global Organic Textile Standard (GOTS)",
      pdf: "./files/Certificate2.pdf",
      preview: "./files/Certificate1.png",
      description:
        "GOTS ensures organic fibers, ethical manufacturing, and strict environmental criteria.",
      issuedBy: "Ecocert Greenlife",
      validity: "2025"
    }
  ];



  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  return (
    <>
      <section className="our-business-section">
        {/* ===== Hero Section ===== */}
        <div className="business-hero" data-aos="fade-up">
          <h2 className="business-title underline-title">Our Business</h2>
          <p className="business-tagline">
            Crafting premium linen fabrics with excellence and innovation.
          </p>
        </div>

        {/* ===== Overview Section ===== */}
        <div className="business-grid">
          <div className="business-text" data-aos="fade-right">
            <h3>About Our Business</h3>
            <p>
              With decades of expertise, <strong>Lis Mount</strong> specializes
              in manufacturing and exporting high-quality linen fabrics.
              We cater to global designers, brands, and retailers, redefining
              eco-luxury textile production through technology and sustainability.
            </p>
            <div className="business-icons">
              <div><FaIndustry className="icon" /> Modern Infrastructure</div>
              <div><FaLeaf className="icon" /> Sustainable Process</div>
              <div><FaHandshake className="icon" /> Global Partnerships</div>
            </div>
          </div>
          <div className="business-image" data-aos="fade-left">
            <img src="files/DSC06891.jpg" alt="Business Overview" />
          </div>
        </div>

        {/* ===== Capabilities Section ===== */}
        <div className="info-section capabilities-section" data-aos="fade-up" style={{ paddingTop: '20px' }}>
          <h3> Our Capabilities</h3>
          <div className="capabilities-grid">
            <div className="capability-card">
              <GiSewingNeedle className="capability-icon" />
              <p>Advanced weaving & dyeing technology</p>
            </div>
            <div className="capability-card">
              <FaIndustry className="capability-icon" />
              <p>Multiple production units worldwide</p>
            </div>
            <div className="capability-card">
              <MdVerified className="capability-icon" />
              <p>Certified quality at every stage</p>
            </div>
            <div className="capability-card">
              <FaCertificate className="capability-icon" />
              <p>GOTS, OCS, ISO certified manufacturing</p>
            </div>
          </div>
        </div>

        {/* ===== Product Lines Section ===== */}
        <div className="info-section" data-aos="fade-up">
          <h3 style={{ paddingTop: '25px' }}>Product Lines & Services</h3>
          <div className="product-grid">
            <div className="product-card">
              <img src="files/DSC06908.jpg" alt="100% Linen" />
              <p>100% Linen Fabrics</p>
            </div>
            <div className="product-card">
              <img src="files/DSC06972.jpg" alt="Blends" />
              <p>Linen-Cotton Blends</p>
            </div>
            <div className="product-card">
              <img src="files/OwnBrand.png" alt="Private Label" />
              <p>Custom / Private Label Production</p>
            </div>
            <div className="product-card">
              <img src="files/Design.png" alt="Collaboration" />
              <p>Design & Technical Collaboration</p>
            </div>
          </div>
        </div>

        {/* ===== Sustainability ===== */}
        <div className="info-section sustainability" data-aos="fade-up">
          <h3>Sustainability & Ethical Practices</h3>
          <p>
            We are deeply committed to eco-friendly sourcing, minimal waste,
            strict chemical standards, and fair labor practices — proving
            that <strong>luxury and responsibility</strong> can coexist beautifully.
          </p>
        </div>

        <div className="certificate-section" data-aos="fade-up" style={{ paddingTop: '30px' }}>
          <h3 className="underline-title">
            <FaCertificate className="heading-icon" /> Our Certificates
          </h3>

          {certificateList.map((cert) => (
            <div key={cert.id} className="cert-accordion">

              {/* HEADER */}
              <div
                className="cert-header"
                onClick={() =>
                  setOpenCertificate(openCertificate === cert.id ? null : cert.id)
                }
              >
                <h4>{cert.title}</h4>

                <span className="view-toggle">
                  {openCertificate === cert.id ? "View Less ▼" : "View More ▶"}
                </span>
              </div>

              {/* CONTENT */}
              {openCertificate === cert.id && (
                <div className="cert-body">
                  <div className="cert-body-content">
                    <img src={cert.preview} alt={cert.title} className="cert-preview" />

                    <div className="cert-details">
                      <p className="cert-desc">{cert.description}</p>
                      <p><strong>Issued By:</strong> {cert.issuedBy}</p>
                      <p><strong>Validity:</strong> {cert.validity}</p>

                      <button
                        className="view-cert-btn"
                        onClick={() => setSelectedPdf(cert.pdf)}
                      >
                        View Certificate PDF
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <hr className="cert-divider" />
            </div>
          ))}
        </div>



        {/* ===== Global Reach ===== */}
        <div className="info-section" data-aos="fade-up">
          <h3>Global Presence</h3>
          <p>
            Our fabrics reach markets across continents, supporting international
            fashion houses and retailers with <strong>precision and passion</strong>.
          </p>
        </div>

        {/* ===== Why Choose Us ===== */}
        <div className="info-section why-choose" data-aos="fade-up">
          <h3 className="why-title">Why Choose Us</h3>
          <div className="why-row">
            <div className="why-card">
              <div className="why-icon"><MdVerified /></div>
              <h5>Uncompromising Quality</h5>
              <p>
                Every thread is crafted with precision and care, ensuring
                the highest international quality standards.
              </p>
            </div>
            <div className="why-card">
              <div className="why-icon"><FaIndustry /></div>
              <h5>Modern Infrastructure</h5>
              <p>
                Equipped with advanced weaving and finishing units for
                large-scale production with perfection.
              </p>
            </div>
            <div className="why-card">
              <div className="why-icon"><FaHandshake /></div>
              <h5>Reliable Partnerships</h5>
              <p>
                We build lasting relationships with clients worldwide based
                on trust, transparency, and timely delivery.
              </p>
            </div>
            <div className="why-card">
              <div className="why-icon"><FaLeaf /></div>
              <h5>Eco-Conscious Process</h5>
              <p>
                Our manufacturing practices focus on sustainability — using
                natural fibers and responsible dyeing methods.
              </p>
            </div>
          </div>
        </div>

        {/* ===== Contact CTA ===== */}
        <div className="business-cta" data-aos="fade-up">
          <button className="btn-primary">Get in Touch</button>
        </div>
      </section>

      {/* ===== PDF Modal ===== */}
      {selectedPdf && (
        <div className="custom-modal-overlay" onClick={() => setSelectedPdf(null)}>
          <div className="custom-modal pdf-modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedPdf(null)}>✕</button>
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer fileUrl={selectedPdf} plugins={[defaultLayoutPluginInstance]} />
            </Worker>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default OurBusiness;
